import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Zap, Crown, Users, X, Loader2, Lock } from "lucide-react";
import { DebateEngineVsChatGPT } from "../components/DebateEngineVsChatGPT";
import { TrustMetrics } from "../components/TrustMetrics";
import { SEO, productLd } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PremiumPage = () => {
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/premium/checkout`);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        // Log premium interest for founder notification
        await axios.post(`${API}/premium/interest`).catch(() => {});
        toast.info("Premium wordt binnenkort gelanceerd! Je interesse is geregistreerd. We informeren je zodra het beschikbaar is.");
      }
    } catch { toast.error("Er ging iets mis"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12" data-testid="premium-page">
      <SEO
        title="Premium — Debate Engine"
        description="Upgrade naar Premium: 6+ AI-debaters, Pro Debate Protocol met Cross-Examination en Arbiter, onbeperkte geschiedenis, geen advertenties. EUR 9,99/maand."
        path="/premium"
        jsonLd={productLd({
          name: "Debate Engine Premium",
          description: "6+ AI-debaters, Pro Debate Protocol (Cross-Examination + Arbiter), onbeperkte geschiedenis, advertentievrij.",
          price: "9.99",
          currency: "EUR",
        })}
      />
      <div className="text-center mb-12">
        <Crown className="w-10 h-10 mx-auto mb-4 text-yellow-500" />
        <h1 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
          PREMIUM
        </h1>
        <p className="text-base text-gray-500 max-w-md mx-auto">
          Ontgrendel de volledige kracht van je AI-expertteam.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {/* Free */}
        <div className="border-2 border-gray-200 p-6 bg-white">
          <h2 className="font-bold text-lg mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Free</h2>
          <p className="text-3xl font-black mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>EUR 0</p>
          <ul className="space-y-3 mb-6">
            {[
              ["check", "4 AI debaters per discussie"],
              ["check", "Alle 38 debater-rollen beschikbaar"],
              ["check", "15 voorgedefinieerde teams"],
              ["check", "3 LLM providers"],
              ["check", "Synthese & verificatie"],
              ["x", "Advertenties aanwezig"],
              ["x", "Beperkte geschiedenis (3 sessies)"],
              ["x", "Max 4 experts per debat"],
            ].map(([type, text]) => (
              <li key={text} className="flex items-center gap-2 text-sm">
                {type === "check" ? <Check className="w-4 h-4 text-green-600 flex-shrink-0" /> : <X className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                <span className={type === "x" ? "text-gray-400" : ""}>{text}</span>
              </li>
            ))}
          </ul>
          <Link to="/" className="block w-full text-center px-4 py-3 border-2 border-gray-200 text-sm font-bold hover:border-gray-400 transition-colors" data-testid="free-plan-btn">
            Huidig Plan
          </Link>
        </div>

        {/* Premium */}
        <div className="border-2 border-[#002FA7] p-6 bg-blue-50 relative">
          <div className="absolute -top-3 left-4 px-2 py-0.5 bg-[#002FA7] text-white text-[10px] font-mono uppercase">Aanbevolen</div>
          <h2 className="font-bold text-lg mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Premium</h2>
          <p className="text-3xl font-black mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>EUR 9,99<span className="text-sm font-normal text-gray-500">/maand</span></p>
          <p className="text-xs text-gray-500 mb-4">Annuleer wanneer je wilt</p>
          <ul className="space-y-3 mb-6">
            {[
              "6+ AI debaters per discussie",
              "Alle 38 debater-rollen beschikbaar",
              "Premium team-composities",
              "Betere LLM-modellen",
              "Geen advertenties",
              "Onbeperkte geschiedenis",
              "Aangepaste teams maken",
              "Prioriteit bij piekbelasting",
            ].map((text) => (
              <li key={text} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                {text}
              </li>
            ))}
          </ul>
          <button onClick={handleUpgrade} disabled={loading}
            className="w-full px-4 py-3 bg-[#2d8cf0] text-white font-bold text-sm hover:bg-[#1a6fd4] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="upgrade-premium-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Ik wil Premium
          </button>
          <p className="text-[11px] text-center text-gray-500 mt-3">Premium wordt binnenkort gelanceerd. Registreer je interesse.</p>
        </div>
      </div>

      {/* FAQ */}
      <DebateEngineVsChatGPT />

      <TrustMetrics />

      <div className="border-t-2 border-gray-200 pt-8">
        <h2 className="font-bold text-lg mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>Veelgestelde Vragen</h2>
        {[
          ["Wat krijg ik met Premium?", "Meer AI-debaters (6+), geen advertenties, betere modellen, onbeperkte geschiedenis, en de mogelijkheid om aangepaste teams samen te stellen."],
          ["Kan ik op elk moment opzeggen?", "Ja, je abonnement is maandelijks opzegbaar. Geen verplichtingen."],
          ["Welke LLM-providers gebruiken jullie?", "We combineren GPT-4o (OpenAI), Claude Sonnet (Anthropic), en Gemini Flash (Google) voor de beste multi-perspectief analyse."],
          ["Zijn alle debater-rollen beschikbaar in Free?", "Ja, alle 38 rollen zijn beschikbaar. Free is gelimiteerd op 4 debaters per sessie, Premium geeft 6+."],
        ].map(([q, a], i) => (
          <details key={q} className="mb-3 border-2 border-gray-200 bg-white" data-testid={`faq-${i}`}>
            <summary className="px-4 py-3 text-sm font-medium cursor-pointer hover:bg-gray-50">{q}</summary>
            <div className="px-4 pb-3 text-sm text-gray-600">{a}</div>
          </details>
        ))}
      </div>
    </div>
  );
};
